import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import AlgoliaMap from './AlgoliaMap';
import MapListingCard from './MapListingCard';
import { fetchListingsInBounds } from './algoliaMapSearch';
import {
  MAX_MAP_HITS,
  SAUDI_CENTER,
  buildCategoryIndex,
  groupListingsByLocation,
  hasMovedEnough,
  isResultCapped,
  toMapListings,
  viewportToBoundingBox,
} from './mapListings';

import css from './AlgoliaMapView.module.css';

const isArabicUi = () =>
  typeof localStorage !== 'undefined' && localStorage.getItem('language') === 'Arabic';

const LOCATE_TIMEOUT_MS = 4000;
// After the customer stops moving the map, wait this long, then search the new area.
const AUTO_SEARCH_DELAY_MS = 1000;
// Ignore tiny nudges: search only when the view moved/zoomed by this share of the last search.
const AUTO_SEARCH_MIN_MOVE = 0.1;
const LOCATE_NOTICE_MS = 4000;

/**
 * Full-width map of services or deals for the customer, in place of the result
 * grid. Pins are listings at their merchant's location, with a price label;
 * tapping one shows a card. Mirrors the mobile app's map screen.
 *
 * `filterParams` / `facetFilters` are the page's own filters for its
 * `listingType`; the other kind only inherits the price cap.
 */
const AlgoliaMapView = props => {
  const {
    listingType,
    filterParams,
    facetFilters,
    keywordQuery,
    initialBounds,
    categories,
    intl,
    // On the home page: no scrolling into view, its own size, and the kind reported up.
    embedded = false,
    className,
    onKindChange,
  } = props;
  const categoryIndex = useMemo(() => buildCategoryIndex(categories), [categories]);
  const pageKind = listingType === 'deals' ? 'deal' : 'service';
  const [kind, setKind] = useState(pageKind);

  // Where to open: the searched place if there is one, else the customer.
  const [origin, setOrigin] = useState(null);
  const [locating, setLocating] = useState(!initialBounds);
  useEffect(() => {
    if (initialBounds || !navigator.geolocation) {
      setLocating(false);
      return undefined;
    }
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      position => {
        if (!cancelled) {
          setOrigin({ lat: position.coords.latitude, lng: position.coords.longitude });
          setLocating(false);
        }
      },
      () => !cancelled && setLocating(false),
      { timeout: LOCATE_TIMEOUT_MS, maximumAge: 600000 }
    );
    return () => {
      cancelled = true;
    };
  }, [initialBounds]);
  // Opening the map from the toggle lands below the filters and category cards; bring it into view.
  useEffect(() => {
    if (!embedded && rootRef.current && rootRef.current.scrollIntoView) {
      rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const originRef = useRef(null);
  originRef.current = origin;

  const [listings, setListings] = useState([]);
  const [totalHits, setTotalHits] = useState(0);
  const [returnedHits, setReturnedHits] = useState(0);
  const [loading, setLoading] = useState(false);
  // The map stopped and a search is about to run: show loading straight away.
  const [pending, setPending] = useState(false);
  // The customer is dragging/zooming: "No listings in this area" is about the
  // area that was searched, so it is hidden until the map settles.
  const [moving, setMoving] = useState(false);
  const movingRef = useRef(false);
  const setMovingOnce = value => {
    if (movingRef.current !== value) {
      movingRef.current = value;
      setMoving(value);
    }
  };
  const [failed, setFailed] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [locateFailed, setLocateFailed] = useState(false);
  const [locatingMe, setLocatingMe] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);

  const rootRef = useRef(null);
  const mapRef = useRef(null);
  const requestIdRef = useRef(0);
  const searchedBoundsRef = useRef(null);
  const currentBoundsRef = useRef(null);
  // The first settled viewport (and any move we make ourselves) is searched
  // straight away; the customer's own moves wait for AUTO_SEARCH_DELAY_MS.
  const searchOnSettleRef = useRef(true);
  const autoSearchTimerRef = useRef(null);
  const cancelAutoSearch = useCallback(() => {
    if (autoSearchTimerRef.current) {
      clearTimeout(autoSearchTimerRef.current);
      autoSearchTimerRef.current = null;
      setPending(false);
    }
  }, []);
  useEffect(() => cancelAutoSearch, [cancelAutoSearch]);
  const kindRef = useRef(kind);
  const latest = useRef({});
  latest.current = { filterParams, facetFilters, keywordQuery, pageKind };

  const groups = useMemo(() => groupListingsByLocation(listings), [listings]);
  const selectedGroup = useMemo(() => groups.find(g => g.key === selectedKey) || null, [
    groups,
    selectedKey,
  ]);

  const search = useCallback(async (bounds, searchKind = kindRef.current) => {
    const requestId = ++requestIdRef.current;
    cancelAutoSearch();
    setPending(false);
    searchedBoundsRef.current = bounds;
    setLoading(true);
    setFailed(false);
    const { filterParams: pageFilters, facetFilters: pageFacets, keywordQuery: query, pageKind: pk } =
      latest.current;
    // Category filters belong to the page's own kind; the other kind keeps the price cap only.
    const sameKind = searchKind === pk;
    try {
      const { hits, nbHits } = await fetchListingsInBounds({
        kind: searchKind,
        bounds: viewportToBoundingBox(bounds),
        query,
        filters: sameKind
          ? pageFilters || []
          : (pageFilters || []).filter(p => p.includes('price.amount')),
        facetFilters: sameKind ? pageFacets : undefined,
        hitsPerPage: MAX_MAP_HITS,
        around: originRef.current || undefined,
      });
      if (requestId !== requestIdRef.current) {
        return;
      }
      setListings(toMapListings(hits, searchKind, originRef.current));
      setTotalHits(nbHits);
      setReturnedHits(hits.length);
      setHasSearched(true);
    } catch (error) {
      if (requestId === requestIdRef.current) {
        // eslint-disable-next-line no-console
        console.error('map listings failed', error);
        setFailed(true);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [cancelAutoSearch]);

  // A new result set may not contain the selected spot any more.
  useEffect(() => {
    if (selectedKey && !groups.some(g => g.key === selectedKey)) {
      setSelectedKey(null);
    }
  }, [groups, selectedKey]);

  const handleIdle = useCallback(
    bounds => {
      currentBoundsRef.current = bounds;
      setMovingOnce(false);
      if (searchOnSettleRef.current) {
        searchOnSettleRef.current = false;
        search(bounds);
        return;
      }
      cancelAutoSearch();
      if (hasMovedEnough(searchedBoundsRef.current, bounds, AUTO_SEARCH_MIN_MOVE)) {
        setPending(true);
        autoSearchTimerRef.current = setTimeout(() => {
          autoSearchTimerRef.current = null;
          search(currentBoundsRef.current);
        }, AUTO_SEARCH_DELAY_MS);
      }
    },
    [search, cancelAutoSearch]
  );

  // The page's filters changed (keyword, price, category): re-run for the same area.
  const filterSignature = JSON.stringify([filterParams, facetFilters, keywordQuery]);
  const firstSignature = useRef(true);
  useEffect(() => {
    if (firstSignature.current) {
      firstSignature.current = false;
      return;
    }
    if (currentBoundsRef.current) {
      setSelectedKey(null);
      search(currentBoundsRef.current);
    }
  }, [filterSignature, search]);

  const handleKindChange = next => {
    if (next === kindRef.current) {
      return;
    }
    kindRef.current = next;
    setKind(next);
    if (onKindChange) {
      onKindChange(next);
    }
    setSelectedKey(null);
    setListings([]);
    if (currentBoundsRef.current) {
      search(currentBoundsRef.current, next);
    }
  };

  const locateNoticeTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(locateNoticeTimerRef.current), []);
  const showLocateFailed = () => {
    setLocateFailed(true);
    clearTimeout(locateNoticeTimerRef.current);
    locateNoticeTimerRef.current = setTimeout(() => setLocateFailed(false), LOCATE_NOTICE_MS);
  };

  const handleLocate = useCallback(() => {
    if (!navigator.geolocation) {
      showLocateFailed();
      return;
    }
    setLocatingMe(true);
    navigator.geolocation.getCurrentPosition(
      position => {
        setLocatingMe(false);
        setLocateFailed(false);
        const here = { lat: position.coords.latitude, lng: position.coords.longitude };
        setOrigin(here);
        originRef.current = here;
        if (mapRef.current) {
          cancelAutoSearch();
          searchOnSettleRef.current = true;
          mapRef.current.panTo(here);
          if ((mapRef.current.getZoom() || 0) < 13) {
            mapRef.current.setZoom(13);
          }
          // No `idle` fires if the map was already there.
          setTimeout(() => {
            if (searchOnSettleRef.current && currentBoundsRef.current) {
              searchOnSettleRef.current = false;
              search(currentBoundsRef.current);
            }
          }, 900);
        }
      },
      () => {
        setLocatingMe(false);
        showLocateFailed();
      },
      { timeout: 2 * LOCATE_TIMEOUT_MS, maximumAge: 60000 }
    );
  }, [search, cancelAutoSearch]);

  const handleSelectGroup = useCallback(group => {
    setSelectedKey(group.key);
    setPageIndex(0);
  }, []);
  const handleMapClick = useCallback(() => setSelectedKey(null), []);

  const titleForGroup = useCallback(
    group =>
      group.listings.length > 1
        ? intl.formatMessage({ id: 'AlgoliaMapView.groupLabel' }, { count: group.listings.length })
        : group.listings[0].title,
    [intl]
  );

  const capped = isResultCapped(totalHits, returnedHits);
  const isEmpty =
    hasSearched && !loading && !pending && !moving && !failed && listings.length === 0;
  const initialCenter = origin || (initialBounds ? null : SAUDI_CENTER);

  const googleBounds = useMemo(
    () =>
      initialBounds
        ? {
            north: initialBounds.ne.lat,
            east: initialBounds.ne.lng,
            south: initialBounds.sw.lat,
            west: initialBounds.sw.lng,
          }
        : null,
    [initialBounds]
  );

  return (
    <div
      ref={rootRef}
      className={classNames(
        css.root,
        { [css.embedded]: embedded, [css.arabicDirection]: isArabicUi() },
        className
      )}
    >
      {locating ? (
        <div className={css.locating}>
          <FormattedMessage id="AlgoliaMapView.locating" />
        </div>
      ) : (
        <AlgoliaMap
          className={css.map}
          center={initialCenter}
          bounds={googleBounds}
          groups={groups}
          selectedKey={selectedKey}
          categoryIndex={categoryIndex}
          titleForGroup={titleForGroup}
          onSelectGroup={handleSelectGroup}
          onMapClick={handleMapClick}
          onIdle={handleIdle}
          onMoveStart={() => {
            cancelAutoSearch();
            setMovingOnce(true);
          }}
          onLocate={handleLocate}
          locateLabel={intl.formatMessage({ id: 'AlgoliaMapView.myLocation' })}
          locating={locatingMe}
          onMapReady={map => {
            mapRef.current = map;
          }}
        />
      )}

      <div className={css.topOverlay}>
        <div className={css.segment} role="tablist">
          {['service', 'deal'].map(option => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={kind === option}
              className={classNames(css.segmentItem, { [css.segmentActive]: kind === option })}
              onClick={() => handleKindChange(option)}
            >
              <FormattedMessage id={option === 'service' ? 'AlgoliaMapView.services' : 'AlgoliaMapView.deals'} />
            </button>
          ))}
        </div>
        {loading || pending ? (
          <div className={classNames(css.pill, css.loadingPill)} role="status">
            <span className={css.spinner} aria-hidden="true" />
            <FormattedMessage id="AlgoliaMapView.loading" />
          </div>
        ) : null}
        {hasSearched && !loading && !pending && !failed && totalHits > 0 ? (
          <div className={css.resultCount} role="status">
            <FormattedMessage id="AlgoliaMapView.results" values={{ count: totalHits }} />
          </div>
        ) : null}
        {!loading && failed ? (
          <button type="button" className={css.pill} onClick={() => search(currentBoundsRef.current)}>
            <FormattedMessage id="AlgoliaMapView.loadFailedRetry" />
          </button>
        ) : null}
        {!loading && !pending && !failed && capped ? (
          <div className={css.notice}>
            <FormattedMessage
              id={kind === 'deal' ? 'AlgoliaMapView.showingSomeDeals' : 'AlgoliaMapView.showingSomeServices'}
              values={{ shown: returnedHits, total: totalHits }}
            />
          </div>
        ) : null}
        {isEmpty ? (
          <div className={css.notice}>
            <FormattedMessage
              id={kind === 'deal' ? 'AlgoliaMapView.noDealsHere' : 'AlgoliaMapView.noServicesHere'}
            />
          </div>
        ) : null}
        {locateFailed ? (
          <div className={css.notice}>
            <FormattedMessage id="AlgoliaMapView.locationUnavailable" />
          </div>
        ) : null}
        {!locating && !origin && !initialBounds && !locateFailed ? (
          <div className={css.notice}>
            <FormattedMessage id="AlgoliaMapView.enableLocation" />
          </div>
        ) : null}
      </div>

      {selectedGroup ? (
        <MapListingCard
          className={css.card}
          group={selectedGroup}
          index={pageIndex}
          onIndexChange={setPageIndex}
          onClose={() => setSelectedKey(null)}
          intl={intl}
        />
      ) : null}
    </div>
  );
};

export default AlgoliaMapView;
